'use client';

import { type FC, useEffect, useRef, useState } from 'react';
import type { WeaponClass } from '@cs-shot/battle-sim';
import {
  weaponSrcFor, TEAM_SKINS, stanceOf, weaponKeyOf, loadKeyedSprite, splitAnchorsFor,
} from './spriteLayout';
import { drawFighter as coreDrawFighter, type FighterParts } from './render/spriteRender';

/**
 * Renders ONE idle fighter (body + chosen weapon) on a canvas using the exact
 * same anchoring/proportions as the battle renderer (`spriteLayout`), so the
 * buy-screen preview matches the fight pixel-for-pixel. A spinner shows while the
 * sprite decodes (no empty space); the sprite is supersampled for crisp downscale.
 */
export const FighterPortrait: FC<{
  side: 't' | 'ct';
  weapon: WeaponClass;
  /** skin file-prefix (e.g. `t1`); default = team's first skin */
  skin?: string;
  /** weapon-skin NAME from the catalog (e.g. `ak_gold`); absent = stock weapon sprite */
  weaponSkin?: string;
  className?: string;
  /** on-screen body height in px */
  heightPx?: number;
}> = ({ side, weapon, skin, weaponSkin, className, heightPx = 210 }) => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const [loaded, setLoaded] = useState(false);

  const W = Math.round(heightPx * 0.95);
  const H = Math.round(heightPx * 1.14);

  useEffect(() => {
    let disposed = false;
    let rafId = 0;
    setLoaded(false);
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Supersample: render at SS× the display size so the canvas only mildly
    // downscales the big source sprite (Safari's one-step downscale is blurry). The
    // final shrink to display size is done by the browser via CSS width/height.
    const SS = Math.max(3, Math.ceil(window.devicePixelRatio || 1));
    const setupCanvas = (cw: number, ch: number) => {
      canvas.width = cw * SS;
      canvas.height = ch * SS;
      canvas.style.width = `${cw}px`;
      canvas.style.height = `${ch}px`;
      ctx.setTransform(SS, 0, 0, SS, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    };

    const stance = stanceOf(weapon);
    const prefix = skin ?? TEAM_SKINS[side][0];
    const split = splitAnchorsFor(prefix);
    if (!split) return () => { disposed = true; };

    // the SHARED renderer (same as the battle) — the lobby portrait STANDS idle (split pose,
    // no run cycle) but breathes: an rAF loop feeds `time`, so the upper body rises/falls over
    // the legs exactly like in the fight.
    Promise.all([
      loadKeyedSprite(`/battle/${prefix}_legs.webp`),
      loadKeyedSprite(`/battle/${prefix}_upper_${stance}.webp`),
      // a chosen weapon skin is addressed BY NAME (that is what the server stores), never by
      // an index into the manifest — the list order is not a stable identifier
      loadKeyedSprite(weaponSkin ? `/battle/wpn_${weaponSkin}.webp` : weaponSrcFor(weaponKeyOf(side, weapon), 0)),
    ])
      .then(([legs, upper, wpn]) => {
        if (disposed) return;
        const cw = Math.ceil(heightPx * 0.95 * 2);
        const ch = Math.ceil(heightPx * 1.14);
        setupCanvas(cw, ch);
        const parts: FighterParts = { legs, upper, weapon: wpn };
        let start = 0;
        const frame = (ts: number) => {
          if (disposed) return;
          if (!start) start = ts;
          ctx.clearRect(0, 0, cw, ch);
          coreDrawFighter(ctx, {
            parts, A: split, stance, x: cw / 2, y: ch * 0.98, tokenH: heightPx,
            dir: 1, aim: 0, moving: false, time: (ts - start) / 1000,
          });
          rafId = requestAnimationFrame(frame);
        };
        setLoaded(true);
        rafId = requestAnimationFrame(frame);
      })
      .catch(() => {});

    return () => { disposed = true; if (rafId) cancelAnimationFrame(rafId); };
  }, [side, weapon, skin, weaponSkin, heightPx]);

  return (
    <div className={className} style={{ position: 'relative', width: W, height: H, display: 'inline-block' }}>
      {!loaded && (
        <div
          aria-hidden
          style={{
            position: 'absolute', top: '50%', left: '50%', width: 26, height: 26,
            marginTop: -13, marginLeft: -13, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.2)', borderTopColor: 'var(--accent-blue, #4ea1ff)',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      )}
      <canvas
        ref={ref}
        style={{
          // always visible: transparent until drawn, then it covers the LQIP — so a
          // missed `loaded` flip can never leave only the blurry placeholder showing
          position: 'absolute', left: '50%', bottom: 0, transform: 'translateX(-50%)',
        }}
      />
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
};

export default FighterPortrait;
